<template>
  <div v-if="isLoading" :class="$style.loadingContainer">
    <div :class="$style.loadingSpinner"></div>
    <div :class="$style.loadingText">Loading...</div>
  </div>
  <div v-else :class="$style.timeTracker">
    <TimeTrackerControls
      :view-mode="viewMode"
      :show-add-task-guide="showAddTaskGuide"
      :add-task-guide-text="addTaskGuideText"
      @show-filter="showFilterDialog = true"
      @toggle-view-mode="toggleViewMode"
      @show-analytics="handleAnalyticsClick"
      @show-actions="showActionsDialog = true"
      @add-task="handleAddTaskButtonClick"
      @close-guide="closeAddTaskGuide"
      @dismiss-guide="dismissAddTaskGuide"
    />

    <!-- In-Between Time Switch - Prominent placement -->
    <InBetweenTimeSwitch
      :is-enabled="trackInBetweenTime"
      @toggle="handleInBetweenTimeToggle"
    />

    <TimeTrackerTaskList
      :tasks="filteredTasks"
      :view-mode="viewMode"
      :selected-period="filters.selectedPeriod.value"
      :date-range="filters.dateRange.value"
      :saturation="saturation"
      :last-created-task-id="lastCreatedTaskId"
      @toggle-task="toggleTask"
      @delete-task="handleDeleteTask"
      @update-task-name="updateTaskName"
      @update-task-duration="updateTaskDuration"
      @update-task-estimate="updateTaskEstimate"
      @update-task-topic="updateTaskTopic"
      @update-task-project="updateTaskProject"
      @toggle-task-completed="toggleTaskCompleted"
      @update-task-sessions="updateTaskSessions"
      @update-task="updateTask"
      @move-sessions="handleMoveSessions"
    />

    <TimeTrackerTotals :filtered-tasks="filteredTasks" />

    <!-- Dialogs -->
    <FilterDialog
      v-model="showFilterDialog"
      :selected-topics="filters.selectedTopics.value"
      :selected-projects="filters.selectedProjects.value"
      :selected-period="filters.selectedPeriod.value"
      :date-range="filters.dateRange.value"
      :selected-completion-status="filters.selectedCompletionStatus.value"
      :filter-by-sessions="filters.filterBySessions.value"
      @update:selected-topics="filters.selectedTopics.value = $event"
      @update:selected-projects="filters.selectedProjects.value = $event"
      @update:selected-period="
        (value) => {
          filters.selectedPeriod.value = value;
        }
      "
      @update:date-range="filters.dateRange.value = $event"
      @update:selected-completion-status="
        (value) => {
          filters.selectedCompletionStatus.value = value;
        }
      "
      @update:filter-by-sessions="filters.filterBySessions.value = $event"
    />
    <AddTaskDialog
      :is-open="showAddTaskDialog"
      @close="showAddTaskDialog = false"
      @add-task="handleAddTask"
    />
    <ActionsDialog
      :is-open="showActionsDialog"
      :saturation="saturation"
      @close="showActionsDialog = false"
      @download="downloadTasks"
      @upload="handleFileUploadWrapper"
      @reset="showResetConfirmDialog = true"
      @load-default-data="loadDefaultData"
      @saturation-change="handleSaturationChange"
      @privacy="showPrivacyDialog = true"
    />
    <ConfirmDialog
      :is-open="showResetConfirmDialog"
      title="Reset All Tasks"
      message="This will permanently delete all tasks and their time data. This action cannot be undone. Are you sure you want to continue?"
      confirm-text="Reset All"
      cancel-text="Cancel"
      @confirm="confirmResetAllTasks"
      @cancel="cancelResetAllTasks"
    />
    <ConfirmDialog
      :is-open="showDeleteConfirmDialog"
      title="Delete Task"
      message="This will permanently delete this task and all its time data. This action cannot be undone. Are you sure you want to continue?"
      confirm-text="Delete"
      cancel-text="Cancel"
      @confirm="confirmDeleteTask"
      @cancel="cancelDeleteTask"
    />

    <!-- Task Overrun Dialog -->
    <TaskOverrunDialog
      :is-open="showOverrunDialog"
      :task-name="overrunTaskName"
      :estimated-duration="overrunEstimatedDuration"
      :actual-duration="overrunActualDuration"
      @close="closeOverrunDialog"
      @save="saveOverrunReasonsWrapper"
      @skip="skipOverrunDialog"
    />

    <!-- Success Message -->
    <SuccessMessage
      v-if="showSuccessMessageDialog"
      :task-name="successTaskName"
      :accuracy="successAccuracy"
    />

    <!-- Analytics Dialog -->
    <AnalyticsDialog
      :is-open="showAnalyticsDialog"
      :tasks="tasks"
      :filters="{
        selectedPeriod: filters.selectedPeriod.value,
        dateRange: filters.dateRange.value,
        selectedTopics: filters.selectedTopics.value,
        selectedProjects: filters.selectedProjects.value,
        selectedCompletionStatus: filters.selectedCompletionStatus.value,
        filterBySessions: filters.filterBySessions.value,
      }"
      @close="showAnalyticsDialog = false"
      @update:filters="handleAnalyticsFiltersUpdate"
    />

    <!-- Privacy Dialog -->
    <PrivacyDialog
      :is-open="showPrivacyDialog"
      @close="showPrivacyDialog = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from "vue";
import { useTaskManager } from "../../composables/useTaskManager";
import { uiSettingsService } from "../../services/uiSettingsService";
import { posthogService } from "../../services/posthogService";
import { isTaskActive } from "../../services/taskService";
import { useTimeTrackerFilters } from "./composables/useTimeTrackerFilters";
import { useTimeTrackerActions } from "./composables/useTimeTrackerActions";
import { useTimeTrackerGuides } from "./composables/useTimeTrackerGuides";
import { useTimeTrackerOverrun } from "./composables/useTimeTrackerOverrun";
import { useTaskOperations } from "../../composables/useTaskOperations";
import { useTaskCompletion } from "../../composables/useTaskCompletion";
import { ViewMode } from "../../types/enums";
import TimeTrackerControls from "./TimeTrackerControls.vue";
import TimeTrackerTaskList from "./TimeTrackerTaskList.vue";
import TimeTrackerTotals from "./TimeTrackerTotals.vue";
import FilterDialog from "../FilterDialog.vue";
import AddTaskDialog from "../AddTaskDialog.vue";
import ActionsDialog from "../ActionsDialog.vue";
import ConfirmDialog from "../ConfirmDialog.vue";
import TaskOverrunDialog from "../TaskOverrunDialog.vue";
import SuccessMessage from "../SuccessMessage.vue";
import AnalyticsDialog from "../AnalyticsDialog/index.vue";
import PrivacyDialog from "../PrivacyDialog.vue";
import InBetweenTimeSwitch from "../InBetweenTimeSwitch.vue";

const {
  tasks,
  toggleTask: originalToggleTask,
  stopTimer,
  initializeTasks,
  syncTaskState,
  completeTask,
  toggleIdleTime,
} = useTaskManager();

// Use composables
const filters = useTimeTrackerFilters();

const {
  downloadTasks,
  handleFileUpload,
  handleResetAllTasks,
  handleLoadDefaultData,
} = useTimeTrackerActions();

const {
  showAddTaskGuide,
  addTaskGuideText,
  closeAddTaskGuide,
  dismissAddTaskGuide,
  showGuideIfNeeded,
  hideGuideOnAddTask,
  showGuideOnDialogClose,
} = useTimeTrackerGuides();

const {
  showOverrunDialog,
  overrunTaskName,
  overrunEstimatedDuration,
  overrunActualDuration,
  showSuccessMessageDialog,
  successTaskName,
  successAccuracy,
  closeOverrunDialog,
  saveOverrunReasons,
  skipOverrunDialog,
  handleTaskCompletion,
} = useTimeTrackerOverrun();

const {
  updateTaskName: updateTaskNameOp,
  updateTaskDuration: updateTaskDurationOp,
  updateTaskEstimate: updateTaskEstimateOp,
  updateTaskTopic: updateTaskTopicOp,
  updateTaskProject: updateTaskProjectOp,
  updateTaskSessions: updateTaskSessionsOp,
  addTask: addTaskOp,
  deleteTask: deleteTaskOp,
  saveTasks,
  getTasks,
} = useTaskOperations();

const { toggleTaskCompleted: toggleTaskCompletedOp } = useTaskCompletion();

// Dialog states
const showFilterDialog = ref(false);
const showResetConfirmDialog = ref(false);
const showAddTaskDialog = ref(false);
const showActionsDialog = ref(false);
const showDeleteConfirmDialog = ref(false);
const taskToDelete = ref<number | null>(null);
const lastCreatedTaskId = ref<number | null>(null);
const viewMode = ref<ViewMode>(
  uiSettingsService.getViewMode() || ViewMode.COMPRESSED
);
const saturation = ref(uiSettingsService.getColorSaturation());
const trackInBetweenTime = ref(uiSettingsService.getTrackInBetweenTime());

// Analytics variables
const showAnalyticsDialog = ref(false);

// Privacy dialog variables
const showPrivacyDialog = ref(false);

// Loading state
const isLoading = ref(true);

// Watch and persist view mode, saturation, and trackInBetweenTime
watch(viewMode, (newMode) => {
  uiSettingsService.setViewMode(newMode);
});

watch(saturation, (newSaturation) => {
  uiSettingsService.setColorSaturation(newSaturation);
});

watch(trackInBetweenTime, (newTrackInBetweenTime) => {
  uiSettingsService.setTrackInBetweenTime(newTrackInBetweenTime);
});

// Watch for tasks becoming empty to show Add Task guide
watch(
  tasks,
  (newTasks) => {
    showGuideIfNeeded(newTasks);
  },
  { deep: true }
);

// Watch for Add Task dialog closing to show guide again if no tasks exist
watch(showAddTaskDialog, (isOpen) => {
  showGuideOnDialogClose(tasks.value, isOpen);
});

// Computed filtered tasks
const filteredTasks = computed(() => {
  return filters.createFilteredTasks(tasks.value);
});

onMounted(async () => {
  try {
    console.log("Starting TimeTracker initialization...");

    // Set a timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      console.warn("Loading timeout reached, forcing loading to complete");
      isLoading.value = false;
    }, 5000); // 5 second timeout

    // Load tasks first with error handling
    try {
      const loadedTasks = getTasks();
      console.log("Tasks loaded successfully:", loadedTasks.length);

      // Initialize the task manager with loaded tasks
      initializeTasks(loadedTasks);

      // Sync task state
      syncTaskState();
    } catch (error) {
      console.error("Error loading tasks:", error);
      // Fallback to empty array if loading fails
      initializeTasks([]);
    }

    // Hide loading immediately after tasks are loaded
    isLoading.value = false;
    clearTimeout(loadingTimeout);
    console.log("Loading state set to false");

    // Defer heavy operations to avoid blocking the UI
    const deferHeavyOperations = () => {
      try {
        console.log("Starting deferred operations...");

        // Recalculate durations from sessions to ensure they're accurate
        const updatedTasks = tasks.value.map((task) => {
          const calculatedDuration =
            task.sessions && task.sessions.length > 0
              ? task.sessions.reduce((total, session) => {
                  if (!session.endTime) {
                    // Active session - calculate from startTime to now
                    return (
                      total +
                      (Date.now() - new Date(session.startTime).getTime())
                    );
                  }
                  const sessionDuration =
                    new Date(session.endTime).getTime() -
                    new Date(session.startTime).getTime();
                  return total + sessionDuration;
                }, 0)
              : task.duration || 0;

          return {
            ...task,
            // Recalculate duration from sessions to ensure it's accurate
            duration: calculatedDuration,
          };
        });

        // Update tasks and sync
        initializeTasks(updatedTasks);
        syncTaskState();
        saveTasks(updatedTasks);
        console.log("Tasks processed and saved");

        // Show guide for Add Task button if no tasks exist
        showGuideIfNeeded(tasks.value);
        console.log("Guide check completed");
      } catch (error) {
        console.error("Error in deferred operations:", error);
      }
    };

    if (typeof requestIdleCallback !== "undefined") {
      requestIdleCallback(deferHeavyOperations);
    } else {
      setTimeout(deferHeavyOperations, 0);
    }
  } catch (error) {
    console.error("Critical error during TimeTracker initialization:", error);
    // Ensure loading is hidden even if there's an error
    isLoading.value = false;
  }
});

function handleAddTask(taskData: {
  name: string;
  topicId: string;
  projectId?: string;
  estimatedDuration: number;
}) {
  console.log("handleAddTask called with:", taskData);

  const newTasks = addTaskOp(taskData);
  console.log("addTaskOp returned:", newTasks.length, "tasks");

  // Track the newly created task
  if (newTasks.length > tasks.value.length) {
    const newTask = newTasks.find(
      (task) => !tasks.value.find((t) => t.id === task.id)
    );
    if (newTask) {
      console.log("Found new task:", newTask);
      lastCreatedTaskId.value = newTask.id;

      // Clear the flag after 10 seconds or when task is started
      setTimeout(() => {
        lastCreatedTaskId.value = null;
      }, 10000);
    }
  }

  console.log("Setting tasks.value to new tasks");
  initializeTasks(newTasks);
  syncTaskState();
}

function handleAddTaskButtonClick() {
  hideGuideOnAddTask();
  showAddTaskDialog.value = true;
}

function handleAnalyticsClick() {
  // Track analytics dialog view
  posthogService.trackAnalyticsViewed();
  // Show the analytics dialog
  showAnalyticsDialog.value = true;
}

function handleAnalyticsFiltersUpdate(updatedFilters: {
  selectedPeriod: string;
  dateRange: { start: string; end: string };
  selectedTopics: string[];
  selectedProjects?: string[];
  selectedCompletionStatus: string;
  filterBySessions: boolean;
}) {
  // Update the local filter state with the new values from analytics dialog
  filters.selectedPeriod.value = updatedFilters.selectedPeriod;
  filters.dateRange.value = updatedFilters.dateRange;
  filters.selectedTopics.value = updatedFilters.selectedTopics;
  if (updatedFilters.selectedProjects) {
    filters.selectedProjects.value = updatedFilters.selectedProjects;
  }
  filters.selectedCompletionStatus.value =
    updatedFilters.selectedCompletionStatus as any;
  filters.filterBySessions.value = updatedFilters.filterBySessions;
}

function confirmResetAllTasks() {
  // Check if there were any in-between tasks that were not completed before resetting
  const hadInBetweenTask = tasks.value.some(
    (task) => task.topicId === "in-between" && !task.completed
  );

  handleResetAllTasks((newTasks: any[]) => {
    initializeTasks(newTasks);
    syncTaskState();
  }, stopTimer);

  // If there was an in-between task that was not completed, turn off Track In-Between Time
  if (hadInBetweenTask) {
    console.log("In-between task was reset, turning off Track In-Between Time");
    trackInBetweenTime.value = false;
    uiSettingsService.setTrackInBetweenTime(false);
  }

  showResetConfirmDialog.value = false;
}

function cancelResetAllTasks() {
  showResetConfirmDialog.value = false;
}

function loadDefaultData() {
  // Check if there were any in-between tasks that were not completed before loading default data
  const hadInBetweenTask = tasks.value.some(
    (task) => task.topicId === "in-between" && !task.completed
  );

  handleLoadDefaultData((newTasks: any[]) => {
    initializeTasks(newTasks);
    syncTaskState();
  }, stopTimer);

  // If there was an in-between task that was not completed, turn off Track In-Between Time
  if (hadInBetweenTask) {
    console.log(
      "In-between task was replaced with default data, turning off Track In-Between Time"
    );
    trackInBetweenTime.value = false;
    uiSettingsService.setTrackInBetweenTime(false);
  }
}

function confirmDeleteTask() {
  if (taskToDelete.value !== null) {
    // Get the task information before deleting it
    const taskToDeleteInfo = tasks.value.find(
      (t) => t.id === taskToDelete.value
    );

    const newTasks = deleteTaskOp(taskToDelete.value, tasks.value);
    initializeTasks(newTasks);
    syncTaskState();
    if (!tasks.value.some((task) => isTaskActive(task))) {
      stopTimer();
    }

    // If the deleted task was an 'In-between' topic task that was not completed, turn off Track In-Between Time
    if (
      taskToDeleteInfo &&
      taskToDeleteInfo.topicId === "in-between" &&
      !taskToDeleteInfo.completed
    ) {
      console.log("In-between task deleted, turning off Track In-Between Time");
      trackInBetweenTime.value = false;
      uiSettingsService.setTrackInBetweenTime(false);
    }

    taskToDelete.value = null;
  }
  showDeleteConfirmDialog.value = false;
}

function cancelDeleteTask() {
  taskToDelete.value = null;
  showDeleteConfirmDialog.value = false;
}

function handleDeleteTask(taskId: number) {
  taskToDelete.value = taskId;
  showDeleteConfirmDialog.value = true;
}

function updateTaskName(taskId: number, name: string) {
  const task = tasks.value.find((t) => t.id === taskId);
  if (task) {
    const newTasks = updateTaskNameOp(task, name);
    initializeTasks(newTasks);
    syncTaskState();
  }
}

function updateTaskDuration(taskId: number, duration: number) {
  const task = tasks.value.find((t) => t.id === taskId);
  if (task) {
    const newTasks = updateTaskDurationOp(task, duration);
    initializeTasks(newTasks);
    syncTaskState();
  }
}

function updateTaskEstimate(taskId: number, estimatedDuration: number) {
  const task = tasks.value.find((t) => t.id === taskId);
  if (task) {
    const newTasks = updateTaskEstimateOp(task, estimatedDuration);
    initializeTasks(newTasks);
    syncTaskState();
  }
}

function updateTaskTopic(taskId: number, topicId: string) {
  const task = tasks.value.find((t) => t.id === taskId);
  if (task) {
    const newTasks = updateTaskTopicOp(task, topicId);
    if (newTasks) {
      initializeTasks(newTasks);
      syncTaskState();
    }
  }
}

function updateTaskProject(taskId: number, projectId?: string) {
  const task = tasks.value.find((t) => t.id === taskId);
  if (task) {
    const newTasks = updateTaskProjectOp(task, projectId);
    if (newTasks) {
      initializeTasks(newTasks);
      syncTaskState();
    }
  }
}

function updateTaskSessions(taskId: number, sessions: any[]) {
  const task = tasks.value.find((t) => t.id === taskId);
  if (task) {
    const newTasks = updateTaskSessionsOp(task, sessions);

    // Recalculate duration from the updated sessions
    const updatedTask = newTasks.find((t) => t.id === taskId);
    if (updatedTask && updatedTask.sessions) {
      const totalDuration = updatedTask.sessions.reduce((total, session) => {
        if (!session.endTime) return total;
        return (
          total +
          (new Date(session.endTime).getTime() -
            new Date(session.startTime).getTime())
        );
      }, 0);
      updatedTask.duration = totalDuration;
    }

    // Directly update the tasks array to preserve sessions
    tasks.value = newTasks;
    // Save the updated tasks to ensure persistence
    saveTasks(newTasks);

    // Force a sync to ensure timer system is aware of changes
    syncTaskState();
  }
}

function updateTask(taskId: number, updatedTask: any) {
  const newTasks = tasks.value.map((t) => (t.id === taskId ? updatedTask : t));
  tasks.value = newTasks;
  saveTasks(tasks.value);
}

function toggleTaskCompleted(taskId: number) {
  const task = tasks.value.find((t) => t.id === taskId);
  if (!task) return;

  // Use completeTask from useTaskManager if marking as completed
  if (!task.completed) {
    completeTask(taskId, trackInBetweenTime.value);
    // Reload tasks to get updated state
    syncTaskState();
    const updatedTask = tasks.value.find((t) => t.id === taskId);
    if (updatedTask) {
      handleTaskCompletion(updatedTask);

      // If the completed task is the in-between task, turn off Track In-Between Time
      if (updatedTask.topicId === "in-between") {
        console.log(
          "In-between task completed, turning off Track In-Between Time"
        );
        trackInBetweenTime.value = false;
        uiSettingsService.setTrackInBetweenTime(false);
      }
    }
  } else {
    // Uncompleting - just toggle the flag
    const updatedTask = toggleTaskCompletedOp(task);
    const newTasks = tasks.value.map((t) =>
      t.id === taskId ? updatedTask : t
    );
    tasks.value = newTasks;
    saveTasks(tasks.value);
  }
}

function toggleTask(taskId: number) {
  console.log("toggleTask called with taskId:", taskId);
  console.log("trackInBetweenTime value:", trackInBetweenTime.value);

  // Clear the new task flag if this task is being started
  if (taskId === lastCreatedTaskId.value) {
    lastCreatedTaskId.value = null;
  }

  // Get the task being toggled
  const task = tasks.value.find((t) => t.id === taskId);
  if (!task) {
    console.error(`Task ${taskId} not found`);
    return;
  }

  // Check if this is an in-between task
  const isInBetweenTask = task.topicId === "in-between";

  if (isInBetweenTask) {
    // Handle in-between task specially - never use recursive tracking
    console.log("Toggling in-between task with trackInBetweenTime = false");
    originalToggleTask(taskId, false);
    return;
  }

  // For regular tasks, handle in-between time tracking
  if (trackInBetweenTime.value) {
    // Check if there's an active in-between task
    const activeInBetweenTask = tasks.value.find(
      (t) => t.topicId === "in-between" && isTaskActive(t)
    );

    if (activeInBetweenTask) {
      // Stop the active in-between task first
      console.log("Stopping active in-between task before starting work task");
      originalToggleTask(activeInBetweenTask.id, false);
    }
  }

  // Call the original toggle function
  console.log("Calling originalToggleTask for work task");
  originalToggleTask(taskId, trackInBetweenTime.value);
}

function toggleViewMode() {
  viewMode.value =
    viewMode.value === ViewMode.COMPRESSED
      ? ViewMode.TIMELINE
      : ViewMode.COMPRESSED;
}

function handleSaturationChange(newSaturation: number) {
  saturation.value = newSaturation;
  uiSettingsService.setColorSaturation(newSaturation);
}

function handleMoveSessions(
  taskId: number,
  data: {
    sessionIndices: number[];
    newTaskName: string;
    newTaskTopicId: string;
    newTaskEstimatedDuration: number;
  }
) {
  const sourceTask = tasks.value.find((t) => t.id === taskId);
  if (!sourceTask || !sourceTask.sessions) return;

  // Create the new task
  const newTaskData = {
    name: data.newTaskName,
    topicId: data.newTaskTopicId,
    estimatedDuration: data.newTaskEstimatedDuration,
  };

  const newTasks = addTaskOp(newTaskData);
  initializeTasks(newTasks);
  syncTaskState();

  // Find the newly created task
  const newTask = newTasks.find(
    (t) => t.name === data.newTaskName && t.topicId === data.newTaskTopicId
  );
  if (!newTask) return;

  // Extract the selected sessions
  const sessionsToMove = data.sessionIndices
    .sort((a, b) => a - b) // Sort in ascending order
    .map((index) => sourceTask.sessions[index])
    .filter((session) => session); // Filter out any undefined sessions

  if (sessionsToMove.length === 0) return;

  // Add sessions to the new task
  const updatedNewTask = {
    ...newTask,
    sessions: sessionsToMove,
    duration: sessionsToMove.reduce((total, session) => {
      if (!session.endTime) {
        // For active sessions, calculate duration up to now
        const currentDuration =
          new Date().getTime() - new Date(session.startTime).getTime();
        return total + currentDuration;
      }
      const duration =
        new Date(session.endTime).getTime() -
        new Date(session.startTime).getTime();
      return total + duration;
    }, 0),
  };

  // Remove sessions from the source task
  const updatedSourceTask = {
    ...sourceTask,
    sessions: sourceTask.sessions.filter(
      (_, index) => !data.sessionIndices.includes(index)
    ),
    duration: sourceTask.sessions
      .filter((_, index) => !data.sessionIndices.includes(index))
      .reduce((total, session) => {
        if (!session.endTime) {
          const currentDuration =
            new Date().getTime() - new Date(session.startTime).getTime();
          return total + currentDuration;
        }
        const duration =
          new Date(session.endTime).getTime() -
          new Date(session.startTime).getTime();
        return total + duration;
      }, 0),
  };

  // Update both tasks
  const finalTasks = tasks.value.map((task) => {
    if (task.id === taskId) {
      return updatedSourceTask;
    }
    if (task.id === newTask.id) {
      return updatedNewTask;
    }
    return task;
  });

  initializeTasks(finalTasks);
  syncTaskState();
  saveTasks(finalTasks);
}

function handleFileUploadWrapper(file: File) {
  // Check if there were any in-between tasks that were not completed before uploading
  const hadInBetweenTask = tasks.value.some(
    (task) => task.topicId === "in-between" && !task.completed
  );

  handleFileUpload(file, tasks.value, (newTasks) => {
    initializeTasks(newTasks);
    syncTaskState();
  });

  // If there was an in-between task that was not completed, turn off Track In-Between Time
  if (hadInBetweenTask) {
    console.log(
      "In-between task was replaced with uploaded data, turning off Track In-Between Time"
    );
    trackInBetweenTime.value = false;
    uiSettingsService.setTrackInBetweenTime(false);
  }
}

function saveOverrunReasonsWrapper(reasons: string[]) {
  saveOverrunReasons(reasons, tasks.value, (newTasks) => {
    initializeTasks(newTasks);
    syncTaskState();
  });
}

function handleInBetweenTimeToggle(enabled: boolean) {
  console.log("handleInBetweenTimeToggle called with enabled:", enabled);
  trackInBetweenTime.value = enabled;
  uiSettingsService.setTrackInBetweenTime(enabled);

  // Use toggleIdleTime from useTaskManager
  toggleIdleTime(enabled);

  // Reload tasks to get updated state
  syncTaskState();
}
</script>

<style module>
.loadingContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 90svh;
  background: #1a1a1a;
  color: white;
}

.loadingSpinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid var(--secondary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.loadingText {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.timeTracker {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
}
</style>
